/**
 * features/quotations/QuotationDetailPage.tsx — ดูรายละเอียด/พิมพ์ใบเสนอราคา
 */

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Download,
  FileCheck2,
  Pencil,
  Trash2,
} from 'lucide-react';
import { formatDate, formatMoney } from '@ft/core';
import { ApiError, resolveApiUrl } from '~/lib/api';
import { PageHeader } from '~/ui/layout';
import { Alert, Button, StatusBadge } from '~/ui/primitives';
import { ConfirmDialog, WarningBanner, useToast } from '~/ui/overlays';
import { DocumentPreviewFrame } from '~/features/documents/DocumentPreviewFrame';
import {
  useDeleteQuotation,
  useIssueTaxInvoiceFromQuotation,
  useQuotation,
  useUpdateQuotationStatus,
} from './hooks';

export function QuotationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const { data: quotation, isLoading } = useQuotation(id);
  const deleteQuotation = useDeleteQuotation();
  const issueTaxInvoice = useIssueTaxInvoiceFromQuotation();
  const updateStatus = useUpdateQuotationStatus();

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [issueOpen, setIssueOpen] = useState(false);

  if (isLoading || !quotation) {
    return <p className="text-sm text-fg-muted">กำลังโหลด…</p>;
  }

  async function handleDelete() {
    if (!id) return;
    try {
      await deleteQuotation.mutateAsync(id);
      toast.success('ลบใบเสนอราคาแล้ว');
      navigate('/quotations');
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'ลบไม่สำเร็จ');
      setDeleteOpen(false);
    }
  }

  async function handleIssueTaxInvoice() {
    if (!id) return;
    try {
      const invoice = await issueTaxInvoice.mutateAsync(id);
      toast.success('ออกใบกำกับภาษีแล้ว');
      navigate(`/tax-invoices/${(invoice as { id: string }).id}`);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'ออกใบกำกับภาษีไม่สำเร็จ');
      setIssueOpen(false);
    }
  }

  async function handleMarkSent() {
    if (!id) return;
    try {
      await updateStatus.mutateAsync({ id, status: 'sent' });
      toast.success('ปรับสถานะเป็น "ส่งแล้ว"');
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'ปรับสถานะไม่สำเร็จ');
    }
  }

  return (
    <div>
      <PageHeader
        title={quotation.documentNumber}
        breadcrumb={
          <button onClick={() => navigate('/quotations')} className="inline-flex items-center gap-1 hover:text-fg">
            <ArrowLeft size={13} />
            ใบเสนอราคา
          </button>
        }
        actions={
          <>
            <a
              href={resolveApiUrl(`/api/quotations/${id}/pdf?download=1`, true)}
              className="btn btn-secondary"
              target="_blank"
              rel="noreferrer"
            >
              <Download size={15} />
              ดาวน์โหลด PDF
            </a>
            {quotation.editable ? (
              <Button variant="secondary" onClick={() => navigate(`/quotations/${id}/edit`)}>
                <Pencil size={15} />
                แก้ไข
              </Button>
            ) : null}
            {quotation.status === 'draft' ? (
              <Button variant="secondary" onClick={() => void handleMarkSent()} loading={updateStatus.isPending}>
                ส่งให้ลูกค้าแล้ว
              </Button>
            ) : null}
            {!quotation.linkedTaxInvoice ? (
              <Button variant="primary" onClick={() => setIssueOpen(true)}>
                <FileCheck2 size={15} />
                ออกใบกำกับภาษี
              </Button>
            ) : null}
            {quotation.editable ? (
              <Button variant="danger" iconOnly onClick={() => setDeleteOpen(true)} aria-label="ลบ">
                <Trash2 size={15} />
              </Button>
            ) : null}
          </>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <StatusBadge status={quotation.status} />
        <span className="text-sm text-fg-muted">ลูกค้า: {quotation.customerName}</span>
        <span className="text-sm text-fg-muted">วันที่: {formatDate(quotation.date)}</span>
        <span className="tabular text-sm font-medium text-fg">
          ยอดรวม {formatMoney(quotation.totalAmount)} บาท
        </span>
      </div>

      {quotation.linkedTaxInvoice ? (
        <div className="mb-4">
          <Alert tone="success" title="ออกใบกำกับภาษีแล้ว">
            <button
              className="link"
              onClick={() => navigate(`/tax-invoices/${quotation.linkedTaxInvoice?.id}`)}
            >
              {quotation.linkedTaxInvoice.documentNumber}
            </button>{' '}
            — ใบเสนอราคานี้จึงไม่สามารถแก้ไขหรือลบได้
          </Alert>
        </div>
      ) : null}

      <DocumentPreviewFrame src={`/api/quotations/${id}/preview`} title={`ใบเสนอราคา ${quotation.documentNumber}`} />

      <ConfirmDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={() => void handleDelete()}
        title="ลบใบเสนอราคา"
        message={`ต้องการลบใบเสนอราคา ${quotation.documentNumber} ใช่หรือไม่ การลบไม่สามารถย้อนกลับได้`}
        confirmLabel="ลบ"
        loading={deleteQuotation.isPending}
      />

      <ConfirmDialog
        open={issueOpen}
        onClose={() => setIssueOpen(false)}
        onConfirm={() => void handleIssueTaxInvoice()}
        title="ออกใบกำกับภาษี"
        message={`ระบบจะออกใบเสร็จ/ใบกำกับภาษีจากใบเสนอราคา ${quotation.documentNumber} และล็อกไม่ให้แก้ไขใบเสนอราคานี้อีก ต้องการดำเนินการต่อหรือไม่`}
        confirmLabel="ออกใบกำกับภาษี"
        tone="primary"
        loading={issueTaxInvoice.isPending}
      />
    </div>
  );
}
